import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../lib/lang-context";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_KEYS = ["gloves", "syringes", "gauze", "masks", "iv", "sutures"];

export default function QuoteForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    products: [],
    message: "",
  });
  const [cats, setCats] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setCats(res.data.categories || []))
      .catch(() => setCats([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCat = (id) =>
    setForm((f) => ({
      ...f,
      products: f.products.includes(id)
        ? f.products.filter((c) => c !== id)
        : [...f.products, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success(t.quote.success);
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        products: [],
        message: "",
      });
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-24 md:py-32 bg-[var(--icor-navy)] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(50% 50% at 100% 0%, rgba(0,82,255,0.25), transparent 60%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: copy + image */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.24em] uppercase font-bold text-blue-300">
              <span className="inline-block w-7 h-px bg-blue-300 align-middle mr-3" />
              {t.quote.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mt-5 text-balance">
              {t.quote.title}
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t.quote.subtitle}
            </p>

            <div
              className="mt-10 hidden md:block overflow-hidden border border-white/10"
              style={{ borderRadius: 4 }}
            >
              <img
                src="https://images.pexels.com/photos/5407249/pexels-photo-5407249.jpeg"
                alt="Doctor using a tablet"
                className="w-full aspect-[16/10] object-cover opacity-90"
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="bg-white dark:bg-zinc-900 text-slate-900 dark:text-white p-8 md:p-10 border border-white/10"
              style={{ borderRadius: 4 }}
              data-testid="quote-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.quote.name} required>
                  <input
                    required
                    data-testid="quote-input-name"
                    className="icor-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                  />
                </Field>
                <Field label={t.quote.company}>
                  <input
                    data-testid="quote-input-company"
                    className="icor-input"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                  />
                </Field>
                <Field label={t.quote.email} required>
                  <input
                    required
                    type="email"
                    data-testid="quote-input-email"
                    className="icor-input"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </Field>
                <Field label={t.quote.phone}>
                  <input
                    data-testid="quote-input-phone"
                    className="icor-input"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </Field>
              </div>

              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                  {t.quote.products}
                </label>
                <div className="flex flex-wrap gap-2">
                  {(cats.length ? cats : CATEGORY_KEYS.map((id) => ({ id }))).map(
                    (c) => {
                      const active = form.products.includes(c.id);
                      const label = c
                        ? lang === "es"
                          ? c.name_es
                          : c.name_en
                        : c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleCat(c.id)}
                          data-testid={`quote-cat-${c.id}`}
                          className={`text-sm px-4 py-2 border transition-all duration-200 ${
                            active
                              ? "bg-[var(--icor-blue)] text-white border-[var(--icor-blue)]"
                              : "bg-white text-slate-700 border-slate-200 hover:border-[var(--icor-navy)]"
                          }`}
                          style={{ borderRadius: 4 }}
                        >
                          {label || c.id}
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="mt-5">
                <Field label={t.quote.message} required>
                  <textarea
                    required
                    data-testid="quote-input-message"
                    rows={5}
                    placeholder={t.quote.messagePlaceholder}
                    className="icor-input resize-none"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit"
                className="mt-8 btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? t.quote.sending : t.quote.submit}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
      {label}
      {required && <span className="text-[var(--icor-blue)] ml-1">*</span>}
    </span>
    {children}
  </label>
);
